/**
 * Track-title data-quality normalization — BUG-185 (mojibake) + BUG-186
 * (filename artifacts + artist duplication).
 *
 * These titles are shown to venue customers on the always-on player screen, so
 * garbled text ("Wolf MÃ¼ller"), DJ-pool tags ("..._PN", "...-2") and
 * duplicated artist prefixes ("Louie Austen - Love Machine" with artist "Louie
 * Austen") are customer-visible defects.
 *
 * Three pure functions, shared by the API upload path, the worker ID3 metadata
 * path and the one-off cleanup script (`apps/api/src/scripts/normalize-track-titles.ts`).
 *
 * Design goal (same policy as the BUG-157 filename parser): flexible but
 * CONSERVATIVE. A false positive — corrupting a legitimate title or damaging
 * already-correct Unicode — is worse than a miss. Every fixer is idempotent.
 */
import { stripFilenameMetadataTokens } from './track-filename.js';
/**
 * Mojibake signature: a UTF-8 string that was decoded once as latin1/cp1252 and
 * re-stored as UTF-8. The tell-tale is a lead char U+00C3 ("Ã", from the
 * UTF-8 lead byte 0xC3), U+00C2 ("Â", 0xC2) or U+00E2 ("â", 0xE2)
 * misread as a single Latin-1 code point, followed by a plausible continuation
 * byte in the 0x80-0xBF range (and, for the 0xC3 family, up to 0xFF). We require
 * BOTH a marker AND a clean lossless reversal before touching anything.
 */
const MOJIBAKE_MARKER = /[\u00C3\u00C2][\u0080-\u00FF]|\u00E2[\u0080-\u00BF]/;
/** C0/C1 control chars a corrupt reversal would introduce (never in a title). */
// Matching control chars IS the point here: their presence is the tell that a
// latin1→UTF-8 reversal went wrong.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/;
/** Strict UTF-8 decoder — throws on any invalid byte sequence (no U+FFFD). */
const UTF8_STRICT = new TextDecoder('utf-8', { fatal: true });
/**
 * Reverse a single latin1->UTF-8 double-encoding (BUG-185).
 *
 * Mechanism: the corrupt string's characters ARE the original UTF-8 bytes, each
 * promoted to a Latin-1 code point. Re-packing them as bytes and decoding as
 * UTF-8 recovers the original text. Uses `TextDecoder` (available in both Node
 * and the browser) so this stays in the isomorphic shared package — no
 * `Buffer`, which the browser player build does not have.
 *
 * Step 0 — Unicode NFC composition. macOS-style NFD storage decomposes the
 * mojibake lead char itself: "Ã" (U+00C3) becomes "A" + U+0303 (combining
 * tilde), and U+0303 > 0xFF, so the ≤0xFF guard would decline a fixable string
 * («CÃ³rdoba» stored as A+U+0303 + ³). Composing to NFC first turns the pieces
 * back into the precomposed U+00C3, after which the reversal works. NFC is also
 * the right canonical storage form generally, so the input is ALWAYS returned in
 * NFC even when no mojibake is present (e.g. decomposed but correct «São»).
 *
 * Guards (all must hold, else the NFC-composed input is returned):
 *   1. Every code point <= 0xFF. A char above 0xFF (Cyrillic, CJK, emoji) cannot
 *      be a promoted byte; packing it as a single byte would TRUNCATE and
 *      corrupt the string. This alone protects all non-Latin scripts.
 *   2. The string matches the mojibake marker. A clean accented-Latin title
 *      ("Café", "Müller", NFC «São») has no U+00C3/U+00C2 lead and is left alone.
 *   3. The reversal decodes cleanly under a STRICT (fatal) UTF-8 decoder — a
 *      "Â" that is not part of a valid UTF-8 pair throws -> guard rejects. As a
 *      belt-and-braces check we also reject any C0/C1 control char.
 *
 * Idempotent: after one pass the result is NFC and contains real accented chars
 * (many above 0xFF, or with no marker), so a second pass is a no-op.
 *
 * Known limitation: only reverses the 0xC2/0xC3/0xE2 (latin1) family, which
 * covers the observed staging corpus. cp1252-specific smart-quote mojibake
 * (bytes 0x80-0x9F) is out of scope — those never appeared in the defect set.
 */
export function fixMojibake(input) {
    if (input.length === 0)
        return input;
    const composed = input.normalize('NFC');
    if (!MOJIBAKE_MARKER.test(composed))
        return composed;
    const bytes = new Uint8Array(composed.length);
    for (let i = 0; i < composed.length; i += 1) {
        const code = composed.charCodeAt(i);
        if (code > 0xff)
            return composed;
        bytes[i] = code;
    }
    let decoded;
    try {
        decoded = UTF8_STRICT.decode(bytes);
    }
    catch {
        return composed;
    }
    if (CONTROL_CHARS.test(decoded))
        return composed;
    return decoded;
}
/**
 * Trailing DJ-pool tag ALLOWLIST. A generic `_[A-Z]{1,4}` pattern over-strips
 * legitimate DJ version tags that use the exact same shape — `Mix_VIP`,
 * `Radio_EDIT`, `Extended_MIX`, `..._DUB`, `..._RMX`. So we match only KNOWN
 * pool codes. `PN` is the only code observed in the real staging corpus; add
 * new codes here as a one-line change (they must be genuine pool markers, never
 * musical version tags like VIP/EDIT/MIX/DUB/RMX).
 */
const POOL_CODES = ['PN'];
const TRAILING_POOL_TAG = new RegExp(`_(?:${POOL_CODES.join('|')})$`);
/**
 * Trailing duplicate-file counter: a hyphen + 1-2 digits at the very end, ONLY
 * when directly preceded by a closing paren ")" — the exact corpus shape
 * "(Original Mix)-2". Anchoring on ")" (rather than any word char) is a
 * deliberate tightening: compound-number titles must survive — "Op. 9-2",
 * "Movement -1", "Track 7-2", "Symphony No. 5-1", "1985-2" all end in a
 * digit/word before "-N" and are left intact.
 */
const TRAILING_DUP_COUNTER = /(?<=\))-\d{1,2}$/;
/**
 * Strip trailing filename artifacts from a title (BUG-186): DJ-pool tags
 * ("_PN") and duplicate-file counters ("-2", " -3"). Applied repeatedly until
 * stable so a "..._PN-3" combination is fully cleaned, then trimmed. Never
 * returns an empty string — a tag-only input keeps its original value.
 */
export function stripFilenameArtifacts(input) {
    let out = input;
    // Loop: a title may carry both a counter and a pool tag ("(Mix)_PN-3").
    for (let i = 0; i < 4; i += 1) {
        const next = out
            .replace(TRAILING_DUP_COUNTER, '')
            .replace(TRAILING_POOL_TAG, '');
        if (next === out)
            break;
        out = next;
    }
    out = out.trimEnd();
    return out.length > 0 ? out : input;
}
/**
 * Filesystem-safe ":" → "_" restoration (residuals follow-up). Uploaders can't
 * put ":" in a filename, so a colon becomes "_": "DJ-Kicks: Sofia Kourtesis"
 * lands as "DJ-Kicks_ Sofia Kourtesis". The tell-tale is an underscore directly
 * followed by a space AND directly preceded by a non-space char (`\S_ `) — a
 * shape near-impossible in a legitimate title:
 *   - "snake_case", "Mix_VIP" have NO space after "_" → untouched.
 *   - a decorative spaced separator " _ " has a space BEFORE "_" → untouched.
 * We restore each such "_ " to ": ".
 *
 * Capture group instead of lookbehind: this shared file may end up in the
 * player bundle, and Safari <16.4 lacks regex lookbehind.
 */
const UNDERSCORE_COLON = /(\S)_ /g;
export function restoreColonFromUnderscore(input) {
    return input.replace(UNDERSCORE_COLON, '$1: ');
}
/**
 * Build the leading-`<artist>` matcher for `stripArtistPrefix`: the bare
 * artist, or the artist followed by a collaboration tail
 * ("feat."/"ft"/"featuring"/"&"/"vs"), then the padded " - " separator.
 * Case-insensitive on the artist. Built per-call — depends on the artist value.
 *
 * `x` is deliberately NOT a collab token here: it over-strips real titles like
 * "Boys x Girls - Anthem" (artist «Boys») down to "Anthem". The remaining
 * tokens are unambiguous collaboration markers.
 */
function buildArtistPrefixRe(artist) {
    const escaped = artist.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`^${escaped}(?:\\s+(?:feat\\.?|ft\\.?|featuring|&|vs\\.?)\\s[^-]*)?\\s*-\\s*`, 'i');
}
/**
 * Separator/whitespace-tolerant comparison key for a multi-artist string.
 * Lowercases, NFC-composes, treats the collaboration separators `,`/`&`/`+` as
 * whitespace, and collapses whitespace runs. So "Superfunk, Ron Carroll",
 * "Superfunk,Ron Carroll" and "Superfunk & Ron Carroll" all key to
 * "superfunk ron carroll". Token ORDER is preserved — a reorder ("Ron Carroll,
 * Superfunk") keys differently and is NOT matched, which keeps the comparison
 * conservative.
 */
function artistKey(s) {
    return s
        .toLowerCase()
        .normalize('NFC')
        .replace(/[,&+]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Strip a leading "<artist> - " from a title when it duplicates the artist
 * field (BUG-186). Two paths, both conservative:
 *
 *   1. Exact/feat regex: the title starts with the artist token (case-
 *      insensitive), optionally a feat-tail, then " - ". Fast and precise.
 *   2. Tolerant multi-artist equality (residuals follow-up): the title's leading
 *      segment (everything before the FIRST padded " - ") compared to the artist
 *      field under `artistKey` — separator/spacing-insensitive but order- and
 *      content-exact. This catches "Superfunk, Ron Carroll - Lucky Star" when
 *      the stored artist uses a different separator ("Superfunk & Ron Carroll").
 *      It NEVER strips a partial artist ("Superfunk" ≠ "superfunk ron carroll")
 *      or a "X - Y" title where X is not the full artist ("Paris - Texas").
 *
 * Returns the title unchanged when `artist` is null/empty, when neither path
 * matches, or when stripping would empty the title.
 */
export function stripArtistPrefix(title, artist) {
    if (!artist || artist.trim().length === 0)
        return title;
    const trimmedArtist = artist.trim();
    const re = buildArtistPrefixRe(trimmedArtist);
    const byRegex = title.replace(re, '').trim();
    if (byRegex !== title.trim() && byRegex.length > 0)
        return byRegex;
    // Tolerant path: split on the FIRST padded " - " and compare keys.
    const sepMatch = title.match(/\s-\s/);
    if (sepMatch && sepMatch.index !== undefined) {
        const leading = title.slice(0, sepMatch.index);
        const rest = title.slice(sepMatch.index + sepMatch[0].length).trim();
        if (rest.length > 0 && artistKey(leading) === artistKey(trimmedArtist)) {
            return rest;
        }
    }
    return title;
}
/**
 * Full artist-field normalization (BUG-185 + BUG-157): NFC-compose + reverse
 * latin1/UTF-8 mojibake (both inside `fixMojibake`), THEN strip the DJ-pool
 * BPM/Camelot block that also leaks into the artist tag ("8A - 103 - Louie
 * Austen" → "Louie Austen"). NFC (step 0) is applied by `fixMojibake` on the
 * raw input before any other transform. This is the SINGLE source of truth for
 * artist cleaning — the worker ID3 path and the cleanup script both call it, so
 * they can never drift. Null-preserving.
 */
export function normalizeTrackArtist(artist) {
    if (artist === null || artist === undefined)
        return null;
    return stripFilenameMetadataTokens(fixMojibake(artist));
}
/**
 * Full title-normalization pipeline for a stored `tracks.title` (+ its
 * `tracks.artist`). Order matters: fix mojibake FIRST (NFC-compose is step 0
 * inside `fixMojibake`) so an artist prefix that was itself garbled ("Wolf
 * MÃ¼ller - ..." with artist "Wolf Müller") matches the clean artist; restore
 * filesystem-safe colons ("_ " → ": "); then strip filename artifacts; then the
 * artist prefix. The artist used for the prefix match is run through the SAME
 * `normalizeTrackArtist` cleaning (mojibake + BPM/Camelot block) so a dirty
 * stored artist like "8A - 103 - Louie Austen" still lets the title's "Louie
 * Austen - " prefix be stripped. Idempotent because each step is.
 */
export function normalizeTrackTitle(title, artist) {
    const deMojibaked = fixMojibake(title);
    const deColoned = restoreColonFromUnderscore(deMojibaked);
    const deArtifacted = stripFilenameArtifacts(deColoned);
    return stripArtistPrefix(deArtifacted, normalizeTrackArtist(artist));
}
//# sourceMappingURL=track-title-normalize.js.map